import React, { Component, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import DockLayout from "./core/layout/DockLayout";
import GlobalHeader from "./core/layout/GlobalHeader";
import ThemeProvider from "./core/theme/ThemeProvider";
import RequireAuth from "./auth/RequireAuth";
import { useAuth } from "./auth/useAuth";

import LoginPage from "./core/pages/LoginPage";
import TeacherLoginPage from "./core/pages/TeacherLoginPage";
import ParentLoginPage from "./core/pages/ParentLoginPage";
import PendingApprovalPage from "./core/pages/PendingApprovalPage";
import NoAccessPage from "./core/pages/NoAccessPage";
import SignupPage from "./core/pages/SignupPage";
import ForgotPasswordPage from "./core/pages/ForgotPasswordPage";
import ResetPasswordPage from "./core/pages/ResetPasswordPage";
import JoinPage from "./core/pages/JoinPage";
import UserProfilePage from "./core/pages/UserProfilePage";
import GlobalLoadingPage from "./core/components/GlobalLoadingPage";
import AddToHomeScreenPrompt from "./core/pwa/AddToHomeScreenPrompt";
import DynamicInstallMetadata from "./core/pwa/DynamicInstallMetadata";
import { TilePreferencesProvider } from "./core/tiles/TilePreferencesProvider";
import mailIcon from "./assets/logos/mail icon.png";
import pagesIcon from "./assets/logos/Pages Icon.png";
import tablesIcon from "./assets/logos/Tables icon.png";
import devicesIcon from "./assets/logos/Devices icon.png";
import classroomIcon from "./assets/logos/classroom icon.png";

import { getModeFromPath } from "./core/utils/getMode";
import { getDefaultPathForHostname, normalizeHostname } from "./core/utils/modeRouting";

const WORKSPACE_LOADING_ICON = "/oikos-edu-icon.svg";
const WORKSPACE_LOADING_COLOR = "#868978";
const MAIL_LOADING_COLOR = "#B9A6FF";
const PAGES_LOADING_COLOR = "#385CFF";
const TABLES_LOADING_COLOR = "#15803d";
const DEVICES_LOADING_COLOR = "#d75c37";
const CLASSROOM_LOADING_COLOR = "#2F6352";

// =========================
// LAZY LOADER
// =========================
const load = (path) =>
  lazy(() =>
    import(`${path}`).catch((error) => ({
      default: () => (
        <div style={{ padding: 20 }}>
          <h2>Could not load this page</h2>
          <p>
            The app could not download this route bundle. Refresh the page, and if this is deployed
            online make sure the host redirects all routes back to <code>index.html</code>.
          </p>
          <pre style={{ whiteSpace: "pre-wrap" }}>{error?.message || `Page not built yet: ${path}`}</pre>
        </div>
      ),
    }))
  );

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("Route render error:", error, info);
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div style={routeErrorStyles.page}>
        <div style={routeErrorStyles.card}>
          <h1 style={routeErrorStyles.title}>This portal could not finish loading</h1>
          <p style={routeErrorStyles.copy}>
            Something crashed while rendering this screen. Refresh once; if it keeps happening, the
            message below will point to the broken deployed route.
          </p>
          <pre style={routeErrorStyles.error}>
            {this.state.error?.message || "Unknown route error"}
          </pre>
        </div>
      </div>
    );
  }
}

const routeErrorStyles = {
  page: {
    alignItems: "center",
    background: "#f8fafc",
    boxSizing: "border-box",
    color: "#0f172a",
    display: "grid",
    minHeight: "100dvh",
    padding: 20,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.08)",
    maxWidth: 680,
    padding: 24,
    width: "100%",
  },
  title: {
    fontSize: 24,
    margin: "0 0 10px",
  },
  copy: {
    color: "#475569",
    lineHeight: 1.6,
    margin: "0 0 16px",
  },
  error: {
    background: "#f1f5f9",
    borderRadius: 12,
    color: "#b91c1c",
    overflowX: "auto",
    padding: 14,
    whiteSpace: "pre-wrap",
  },
};

// =========================
// PAGES
// =========================
const CampusDashboard = load("./platforms/campus/pages/CampusDashboardPage");
const TeacherPortalApp = load("./platforms/campus/portals/teacher/TeacherPortalApp");
const ParentPortalApp = load("./platforms/campus/portals/parent/ParentPortalApp");
const CampusEnrollmentPublicPage = load("./platforms/campus/pages/CampusEnrollmentPublicPage");
const AdminDashboardPage = load("./platforms/admin/pages/AdminDashboardPage");
const EduAdminPage = load("./platforms/edu/pages/EduAdminPage");
const EduOrganizationAdminPage = load("./platforms/edu/pages/EduOrganizationAdminPage");
const EduHallPassPage = load("./platforms/edu/pages/EduHallPassPage");
const EduLandingPage = load("./platforms/edu/pages/EduLandingPage");
const EduSalesPage = load("./platforms/edu/pages/EduSalesPage");
const EduWorkspaceAdminPage = load("./platforms/edu/pages/EduWorkspaceAdminPage");
const EduWorkspacePortalPage = load("./platforms/edu/pages/EduWorkspacePortalPage");
const OikosMailPage = load("./platforms/edu/pages/OikosMailPage");
const TeacherBoardAdminPage = load("./platforms/edu/pages/TeacherBoardAdminPage");
const TeacherBoardPortalPage = load("./platforms/edu/pages/TeacherBoardPortalPage");
const TeacherBoardDisplayPage = load("./platforms/edu/pages/TeacherBoardDisplayPage");
const DevicesPortalPage = load("./platforms/edu/pages/DevicesPortalPage");
const EduTeacherPortalPage = load("./platforms/edu/pages/EduTeacherPortalPage");
const StudentDevicePage = load("./platforms/edu/pages/StudentDevicePage");
const ClassroomAppPage = load("./platforms/classroom/pages/ClassroomAppPage");
const PagesDashboard = load("./platforms/pages/PagesDashboard");
const TablesDashboard = load("./platforms/pages/TablesDashboard");

// =========================
// MODE WRAPPER
// =========================
function ModeWrapper({ children }) {
  const location = useLocation();

  const mode = getModeFromPath(
    location.pathname,
    window.location.hostname
  );

  return <ThemeProvider mode={mode}>{children}</ThemeProvider>;
}

// =========================
// ROOT REDIRECT
// =========================
function HomeOrDomain() {
  return <Navigate to={getDefaultPathForHostname(window.location.hostname)} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const { user, loading: authLoading, profileReady } = useAuth();
  const path = location.pathname;
  const hostname = normalizeHostname(window.location.hostname);
  const isEduAppHost = hostname === "oikosedu.app" || hostname === "www.oikosedu.app";
  const isCampusAppPath =
    path === "/campus" ||
    path === "/campus/" ||
    path.startsWith("/campus/students") ||
    path.startsWith("/campus/staff") ||
    path.startsWith("/campus/attendance") ||
    path.startsWith("/campus/enrollment") ||
    path.startsWith("/campus/tuition") ||
    path.startsWith("/campus/communication") ||
    path.startsWith("/campus/system-integrations") ||
    path.startsWith("/campus/settings");

  if (isEduAppHost && (path === "/admin" || path.startsWith("/admin/"))) {
    return <Navigate to="/globaladmin" replace />;
  }

  if (path === "/edu/devices" || path.startsWith("/edu/devices/")) {
    return <Navigate to={`${path.replace(/^\/edu\/devices/, "/devices")}${location.search || ""}`} replace />;
  }

  const isPublicRoute =
    path === "/login" ||
    path.startsWith("/login/") ||
    path === "/teacher/login" ||
    path === "/campus/teacher/login" ||
    path.startsWith("/campus/teacher/login/") ||
    path === "/parent/login" ||
    path === "/edu/sales" ||
    path === "/edu/hallpass" ||
    path.startsWith("/edu/hallpass/") ||
    path === "/devices" ||
    path === "/profile" ||
    path === "/devices/profile" ||
    path === "/devices/admin" ||
    path.startsWith("/devices/admin/") ||
    path === "/devices/teacher" ||
    path.startsWith("/devices/teacher/") ||
    path === "/devices/teachers" ||
    path === "/devices/teachers/login" ||
    path.startsWith("/devices/teachers/login/") ||
    path === "/devices/teacher/login" ||
    path.startsWith("/devices/teacher/login/") ||
    path === "/devices/student" ||
    path.startsWith("/devices/student/") ||
    path === "/workspace" ||
    path.startsWith("/workspace/") ||
    path === "/teacherboard" ||
    path.startsWith("/teacherboard/") ||
    path === "/mail" ||
    path.startsWith("/mail/") ||
    path === "/pages" ||
    path.startsWith("/pages/") ||
    path === "/tables" ||
    path.startsWith("/tables/") ||
    path === "/workspace/admin" ||
    path.startsWith("/workspace/admin/") ||
    path === "/student" ||
    path.startsWith("/student/") ||
    path === "/edu/teacher-login" ||
    path.startsWith("/edu/teacher-login/") ||
    path === "/studentdevice" ||
    path.startsWith("/studentdevice/") ||
    path === "/studentdevices" ||
    path.startsWith("/studentdevices/") ||
    path === "/classroom/student" ||
    path.startsWith("/classroom/student/") ||
    path === "/signup" ||
    path === "/join" ||
    path.startsWith("/campus/enroll/") ||
    path === "/forgot-password" ||
    path === "/reset-password" ||
    path === "/pending-approval" ||
    path.startsWith("/no-access");

  if (path === "/teacher/login" || path === "/campus/teacher/login" || path.startsWith("/campus/teacher/login/")) {
    return (
      <Routes>
        <Route path="/teacher/login" element={<TeacherLoginPage />} />
        <Route path="/campus/teacher/login" element={<TeacherLoginPage />} />
        <Route path="/campus/teacher/login/:schoolCode" element={<TeacherLoginPage />} />
        <Route path="*" element={<Navigate to="/teacher/login" replace />} />
      </Routes>
    );
  }

  if (path === "/parent/login") {
    return (
      <Routes>
        <Route path="/parent/login" element={<ParentLoginPage />} />
        <Route path="*" element={<Navigate to="/parent/login" replace />} />
      </Routes>
    );
  }

  if (isCampusAppPath && !authLoading && profileReady && !user) {
    return (
      <Routes>
        <Route path="/campus/*" element={<LoginPage forcedOriginalPath={path} forcedMode="campus" />} />
        <Route path="*" element={<Navigate to="/campus" replace />} />
      </Routes>
    );
  }

  if (path === "/teacher" || path.startsWith("/teacher/")) {
    return (
      <TilePreferencesProvider>
        <ModeWrapper>
          <GlobalHeader />
          <DockLayout>
            <Routes>
              <Route path="/teacher/*" element={<TeacherPortalApp />} />
              <Route path="*" element={<Navigate to="/teacher" replace />} />
            </Routes>
          </DockLayout>
        </ModeWrapper>
      </TilePreferencesProvider>
    );
  }

  if (
    path === "/campus/teacher" ||
    path.startsWith("/campus/teacher/") ||
    path === "/campus/teachers" ||
    path.startsWith("/campus/teachers/")
  ) {
    return (
      <TilePreferencesProvider>
        <ModeWrapper>
          <GlobalHeader />
          <DockLayout>
            <Routes>
              <Route path="/campus/teachers" element={<Navigate to="/campus/teacher" replace />} />
              <Route path="/campus/teachers/*" element={<Navigate to="/campus/teacher" replace />} />
              <Route path="/campus/teacher/*" element={<TeacherPortalApp />} />
              <Route path="*" element={<Navigate to="/campus/teacher" replace />} />
            </Routes>
          </DockLayout>
        </ModeWrapper>
      </TilePreferencesProvider>
    );
  }

  if (path.startsWith("/parent")) {
    return (
      <ModeWrapper>
        <Routes>
          <Route path="/parent/*" element={<ParentPortalApp />} />
          <Route path="*" element={<Navigate to="/parent" replace />} />
        </Routes>
      </ModeWrapper>
    );
  }

  if (
    path === "/edu/teacher" ||
    path.startsWith("/edu/teacher/")
  ) {
    return <Navigate to="/devices/teacher" replace />;
  }

  if (path === "/classroom" || path.startsWith("/classroom/")) {
    return (
      <TilePreferencesProvider>
        <ModeWrapper>
          <Routes>
            <Route path="/classroom" element={<ClassroomAppPage role="select" />} />
            <Route path="/classroom/admin" element={<ClassroomAppPage role="admin" />} />
            <Route path="/classroom/admin/:schoolCode" element={<ClassroomAppPage role="admin" />} />
            <Route path="/classroom/teacher" element={<ClassroomAppPage role="teacher" />} />
            <Route path="/classroom/teacher/:schoolCode" element={<ClassroomAppPage role="teacher" />} />
            <Route path="/classroom/student" element={<ClassroomAppPage role="student" />} />
            <Route path="/classroom/student/:schoolCode" element={<ClassroomAppPage role="student" />} />
            <Route path="/classroom/students" element={<ClassroomAppPage role="student" />} />
            <Route path="/classroom/students/:schoolCode" element={<ClassroomAppPage role="student" />} />
            <Route path="*" element={<Navigate to="/classroom" replace />} />
          </Routes>
        </ModeWrapper>
      </TilePreferencesProvider>
    );
  }

  if (path === "/edu") {
    return (
      <RequireAuth>
        <ThemeProvider mode="edu">
          <EduLandingPage />
        </ThemeProvider>
      </RequireAuth>
    );
  }

  if (path === "/edu/admin" || path.startsWith("/edu/admin/")) {
    return (
      <RequireAuth>
        <ThemeProvider mode="edu">
          <EduOrganizationAdminPage />
        </ThemeProvider>
      </RequireAuth>
    );
  }

  if (path === "/globaladmin" || path.startsWith("/globaladmin/")) {
    return (
      <RequireAuth>
        <ThemeProvider mode="admin">
          <AdminDashboardPage />
        </ThemeProvider>
      </RequireAuth>
    );
  }

  if (path === "/teacherboard/admin" || path.startsWith("/teacherboard/admin/")) {
    return (
      <RequireAuth>
        <ThemeProvider mode="edu">
          <TeacherBoardAdminPage />
        </ThemeProvider>
      </RequireAuth>
    );
  }

  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/:schoolCode" element={<LoginPage />} />
        <Route path="/teacher/login" element={<TeacherLoginPage />} />
        <Route path="/parent/login" element={<ParentLoginPage />} />
        <Route path="/edu/sales" element={<EduSalesPage />} />
        <Route path="/edu/hallpass" element={<EduHallPassPage />} />
        <Route path="/edu/hallpass/:portalMode" element={<EduHallPassPage />} />
        <Route path="/edu/hallpass/:portalMode/:schoolCode" element={<EduHallPassPage />} />
        <Route path="/edu/hallpass/:portalMode/:schoolCode/:hallPassPage" element={<EduHallPassPage />} />
        <Route path="/devices" element={<DevicesPortalPage role="select" />} />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <TilePreferencesProvider>
                <ThemeProvider mode="edu">
                  <UserProfilePage />
                </ThemeProvider>
              </TilePreferencesProvider>
            </RequireAuth>
          }
        />
        <Route path="/devices/profile" element={<Navigate to="/profile" replace />} />
        <Route path="/devices/admin" element={<DevicesPortalPage role="admin" />} />
        <Route path="/devices/admin/:schoolCode" element={<DevicesPortalPage role="admin" />} />
        <Route path="/devices/teacher" element={<DevicesPortalPage role="teacher" />} />
        <Route path="/devices/teacher/:schoolCode" element={<DevicesPortalPage role="teacher" />} />
        <Route path="/devices/teachers" element={<EduTeacherPortalPage />} />
        <Route path="/devices/teachers/login" element={<Navigate to="/devices/teacher" replace />} />
        <Route path="/devices/teachers/login/:schoolCode" element={<DevicesPortalPage role="teacher" />} />
        <Route path="/devices/teacher/login" element={<Navigate to="/devices/teacher" replace />} />
        <Route path="/devices/teacher/login/:schoolCode" element={<DevicesPortalPage role="teacher" />} />
        <Route path="/devices/student" element={<StudentDevicePage />} />
        <Route path="/devices/student/:schoolCode" element={<StudentDevicePage />} />
        <Route path="/workspace" element={<EduWorkspacePortalPage role="select" />} />
        <Route path="/workspace/admin" element={<EduWorkspacePortalPage role="admin" />} />
        <Route path="/workspace/admin/:schoolCode" element={<EduWorkspacePortalPage role="admin" />} />
        <Route path="/workspace/teacher" element={<EduWorkspacePortalPage role="teacher" />} />
        <Route path="/workspace/teacher/:schoolCode" element={<EduWorkspacePortalPage role="teacher" />} />
        <Route path="/workspace/student" element={<EduWorkspacePortalPage role="student" />} />
        <Route path="/workspace/student/:schoolCode" element={<EduWorkspacePortalPage role="student" />} />
        <Route path="/teacherboard" element={<TeacherBoardPortalPage />} />
        <Route path="/teacherboard/:schoolCode" element={<TeacherBoardPortalPage />} />
        <Route path="/teacherboard/:schoolCode/:teacherSlug" element={<TeacherBoardDisplayPage />} />
        <Route path="/mail" element={<OikosMailPage />} />
        <Route path="/mail/inbox" element={<OikosMailPage />} />
        <Route path="/mail/sent" element={<OikosMailPage />} />
        <Route path="/mail/trash" element={<OikosMailPage />} />
        <Route path="/mail/tag/:tagId" element={<OikosMailPage />} />
        <Route path="/mail/compose" element={<OikosMailPage />} />
        <Route path="/mail/thread/:threadId" element={<OikosMailPage />} />
        <Route path="/mail/admin/*" element={<Navigate to="/workspace/admin" replace />} />
        <Route path="/pages" element={<PagesDashboard />} />
        <Route path="/pages/*" element={<PagesDashboard />} />
        <Route path="/tables" element={<TablesDashboard />} />
        <Route path="/tables/*" element={<TablesDashboard />} />
        <Route path="/student" element={<Navigate to="/devices/student" replace />} />
        <Route path="/student/:schoolCode" element={<StudentDevicePage />} />
        <Route path="/edu/teacher-login" element={<Navigate to="/devices/teacher" replace />} />
        <Route path="/edu/teacher-login/:schoolCode" element={<Navigate to="/devices/teacher" replace />} />
        <Route path="/studentdevice" element={<Navigate to="/devices/student" replace />} />
        <Route path="/studentdevice/:schoolCode" element={<StudentDevicePage />} />
        <Route path="/studentdevices" element={<Navigate to="/devices/student" replace />} />
        <Route path="/studentdevices/:schoolCode" element={<StudentDevicePage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/campus/enroll/:publicCode" element={<CampusEnrollmentPublicPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
        <Route path="/no-access/*" element={<NoAccessPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <RequireAuth>
      <TilePreferencesProvider>
        <ModeWrapper>
          <GlobalHeader />
          <DockLayout>
            <Routes>
              <Route path="/" element={<HomeOrDomain />} />

              <Route path="/home" element={<Navigate to="/edu" replace />} />
              <Route path="/business" element={<Navigate to="/edu" replace />} />
              <Route path="/EDU" element={<Navigate to="/edu" replace />} />
              <Route path="/edu" element={<Navigate to="/edu" replace />} />
              <Route path="/edu/admin" element={<EduOrganizationAdminPage />} />
              <Route path="/edu/admin/*" element={<EduOrganizationAdminPage />} />
              <Route path="/edu/teacher" element={<Navigate to="/devices/teacher" replace />} />
              <Route path="/edu/teacher/*" element={<Navigate to="/devices/teacher" replace />} />
              <Route path="/workspace" element={<Navigate to="/workspace/admin" replace />} />
              <Route path="/workspace/admin" element={<EduWorkspaceAdminPage />} />
              <Route path="/teacherboard/admin" element={<TeacherBoardAdminPage />} />
              <Route path="/teacherboard" element={<TeacherBoardPortalPage />} />
              <Route path="/teacherboard/:schoolCode" element={<TeacherBoardPortalPage />} />
              <Route path="/teacherboard/:schoolCode/:teacherSlug" element={<TeacherBoardDisplayPage />} />
              <Route path="/mail/*" element={<OikosMailPage />} />
              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/devices/profile" element={<Navigate to="/profile" replace />} />
              <Route path="/devices/admin" element={<Navigate to="/edu/admin?section=devices" replace />} />
              <Route path="/devices/teachers" element={<EduTeacherPortalPage />} />
              <Route path="/edu/hallpass" element={<EduHallPassPage />} />
              <Route path="/edu/hallpass/:portalMode" element={<EduHallPassPage />} />
              <Route path="/edu/hallpass/:portalMode/:schoolCode" element={<EduHallPassPage />} />
              <Route path="/edu/hallpass/:portalMode/:schoolCode/:hallPassPage" element={<EduHallPassPage />} />
              <Route path="/classroom/admin" element={<ClassroomAppPage role="admin" />} />
              <Route path="/classroom/teacher" element={<ClassroomAppPage role="teacher" />} />
              <Route path="/classroom/teacher/:schoolCode" element={<ClassroomAppPage role="teacher" />} />
              <Route path="/classroom/student" element={<ClassroomAppPage role="student" />} />
              <Route path="/classroom/student/:schoolCode" element={<ClassroomAppPage role="student" />} />
              <Route path="/nightstand" element={<Navigate to="/edu" replace />} />

              <Route path="/admin" element={<Navigate to="/globaladmin" replace />} />
              <Route path="/admin/*" element={<Navigate to="/globaladmin" replace />} />
              <Route path="/globaladmin" element={<AdminDashboardPage />} />
              <Route path="/globaladmin/*" element={<AdminDashboardPage />} />
              <Route path="/campus/teachers" element={<Navigate to="/campus/teacher" replace />} />
              <Route path="/campus/teachers/*" element={<Navigate to="/campus/teacher" replace />} />
              <Route path="/campus/teacher/*" element={<TeacherPortalApp />} />
              <Route path="/campus/*" element={<CampusDashboard />} />
              <Route path="/pages" element={<PagesDashboard />} />
              <Route path="/tables" element={<TablesDashboard />} />
              <Route path="/tables/*" element={<TablesDashboard />} />
              <Route path="/sports" element={<Navigate to="/edu" replace />} />
              <Route path="/farm" element={<Navigate to="/edu" replace />} />

              <Route path="*" element={<HomeOrDomain />} />
            </Routes>
          </DockLayout>
        </ModeWrapper>
      </TilePreferencesProvider>
    </RequireAuth>
  );
}

function AppShell() {
  const location = useLocation();
  const path = location.pathname.toLowerCase();
  const loadingFallback = path.startsWith("/workspace") ? (
    <GlobalLoadingPage
      title="Loading Workspace"
      detail="Preparing your organization sign in."
      modeOverride="edu"
      primaryColorOverride={WORKSPACE_LOADING_COLOR}
      iconOverride={WORKSPACE_LOADING_ICON}
      iconFallbackOverride={WORKSPACE_LOADING_ICON}
      labelOverride="Workspace"
    />
  ) : path.startsWith("/mail") ? (
    <GlobalLoadingPage
      title="Loading Oikos Mail"
      detail="Opening the internal mailbox."
      modeOverride="edu"
      primaryColorOverride={MAIL_LOADING_COLOR}
      iconOverride={mailIcon}
      iconFallbackOverride={mailIcon}
      labelOverride="Mail"
    />
  ) : path.startsWith("/devices") || path.startsWith("/studentdevice") || path.startsWith("/studentdevices") ? (
    <GlobalLoadingPage
      title="Preparing Student Device"
      detail="Getting your school desktop ready."
      modeOverride="devices"
      primaryColorOverride={DEVICES_LOADING_COLOR}
      iconOverride={devicesIcon}
      iconFallbackOverride={devicesIcon}
      labelOverride="Managed Devices"
    />
  ) : path.startsWith("/pages") ? (
    <GlobalLoadingPage
      title="Loading Pages"
      detail="Opening your documents."
      modeOverride="pages"
      primaryColorOverride={PAGES_LOADING_COLOR}
      iconOverride={pagesIcon}
      iconFallbackOverride={pagesIcon}
      labelOverride="Pages"
    />
  ) : path.startsWith("/tables") ? (
    <GlobalLoadingPage
      title="Loading Tables"
      detail="Opening your tables."
      modeOverride="tables"
      primaryColorOverride={TABLES_LOADING_COLOR}
      iconOverride={tablesIcon}
      iconFallbackOverride={tablesIcon}
      labelOverride="Tables"
    />
  ) : path.startsWith("/classroom") ? (
    <GlobalLoadingPage
      title="Loading Classroom"
      detail="Opening your classes and assignments."
      modeOverride="classroom"
      primaryColorOverride={CLASSROOM_LOADING_COLOR}
      iconOverride={classroomIcon}
      iconFallbackOverride={classroomIcon}
      labelOverride="Classroom"
    />
  ) : (
    <GlobalLoadingPage
      title="Loading Page"
      detail="Pulling in the next screen and getting everything ready..."
    />
  );

  return (
    <Suspense fallback={loadingFallback}>
      <DynamicInstallMetadata />
      <AddToHomeScreenPrompt />
      <RouteErrorBoundary resetKey={location.pathname}>
        <AppRoutes />
      </RouteErrorBoundary>
    </Suspense>
  );
}

// =========================
// APP
// =========================
export default function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppShell />
    </Router>
  );
}
